import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useMount } from 'ahooks';
import { Button, Checkbox, Input, message, Pagination, Popconfirm, Select } from 'antd';
import Empty from 'components/Empty';
import { useFormikContext } from 'formik';
import { useIsStoreAliasFirst } from 'hooks/business/store/useStoreName';
import useCrud from 'hooks/useCrud';
import useSmartField from 'hooks/useSmartField';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { itemList, ItemListParams, KeywordType } from './api';
import Item from './Item';
import { FV, getItemsCheckedNext, isItemChecked, isItemSelected } from './share';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border: solid 1px #f0f2f5;
  > header {
    flex: 0 0 44px;
    display: flex;
    align-items: center;
    padding: 0 16px;
    border-bottom: solid 1px #f0f2f5;
  }
  > div {
    flex: 1 0 0;
    position: relative;
    padding: 0 16px;
    overflow: auto;
    > section > div {
      margin-bottom: 10px;
    }
  }
`;

const Selected = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 10px;
  margin-right: 10px;
  background: #f0f2f5;
  border-radius: 2px;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  padding: 12px 0;
  background: #fff;
  z-index: 1;
  > * {
    flex: 0 0 auto;
    width: auto;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: sticky;
  bottom: 0;
  margin: 0 -16px;
  padding: 12px 16px;
  background: #fff;
  box-shadow: 0 -2px 8px 0 rgba(167, 178, 204, 0.1), inset 0 1px 0 0 #f0f2f5;
  z-index: 1;
`;

function ItemList() {
  const { t } = useTranslate();

  const keywordTypeLabelMap: Record<KeywordType, string> = {
    1: t('sider.商品名称'),
    2: t('sider.商品ID'),
    3: t('aide.主商品货号'),
    4: t('sider.SKU货号'),
  };

  const {
    values: { platform, region, stores, selectFlag },
  } = useFormikContext<FV>();

  const isStoreAliasFirst = useIsStoreAliasFirst(platform, region);

  const storeNameMap = useMemo(() => {
    return stores.reduce<Record<string, string>>((res, store) => {
      const { storeId, storeName, alias } = store;

      res[storeId] = isStoreAliasFirst ? alias || storeName : storeName;

      return res;
    }, {});
  }, [isStoreAliasFirst, stores]);

  const {
    field: { value, onChange },
  } = useSmartField<FV['selectedItems']>('selectedItems');

  const [selectedOnly, setSelectedOnly] = useState(false);

  const [keywordType, setKeywordType] = useState<KeywordType>(1);

  const [keyword, setKeyword] = useState('');

  const [page, setPage] = useState({ current: 1, pageSize: 10, total: 0 });

  const { loading, list, fetchList } = useCrud({
    readList: useCallback((params: ItemListParams) => {
      return itemList(params).then((res) => {
        const {
          pageInfo: { pageNo: current, pageSize, totalCount: total },
          itemList,
        } = res;

        setPage({
          current,
          pageSize,
          total,
        });

        return itemList;
      });
    }, []),
  });

  const refreshList = (params?: Partial<ItemListParams>) => {
    const trimedKeyword = keyword.trim();

    return fetchList({
      externalType: platform,
      region,
      externalStoreId: stores.map(({ storeId }) => storeId),
      keywordType: trimedKeyword ? keywordType : undefined,
      keyword: trimedKeyword || undefined,
      spuId: selectedOnly ? value.map(({ spuId }) => spuId) : undefined,
      pageNo: page.current,
      pageSize: page.pageSize,
      ...params,
    }).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  };

  useMount(refreshList);

  const refsCurrent = { selectedOnly, refreshList };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { selectedOnly, refreshList },
    } = refs;

    if (selectedOnly) {
      refreshList();
    }
  }, [selectFlag]);

  const allChecked = useMemo(() => {
    return (list || []).every((item) => isItemChecked(value, item));
  }, [list, value]);

  const allIndeterminate = useMemo(() => {
    if (allChecked) {
      return false;
    }

    return (list || []).some((item) => {
      const target = value.find((selectedItem) => isItemSelected(selectedItem, item));

      return target !== undefined;
    });
  }, [list, value, allChecked]);

  return (
    <Container>
      <header>
        <Selected>
          {t('voucher.已选x', { x: value.length })}
          {value.length > 0 && (
            <Popconfirm
              placement="bottom"
              title={t('voucher.您确定要清空吗？')}
              onConfirm={() => {
                onChange([]);

                if (selectedOnly) {
                  setSelectedOnly(false);

                  refreshList({ pageNo: 1, spuId: undefined });
                }

                message.success(t('aide.清空成功'));
              }}
            >
              <Button type="link">{t('voucher.清空已选')}</Button>
            </Popconfirm>
          )}
        </Selected>
        {(selectedOnly || value.length > 0) && (
          <Checkbox
            checked={selectedOnly}
            onChange={({ target: { checked } }) => {
              setSelectedOnly(checked);

              refreshList({
                pageNo: 1,
                spuId: checked ? value.map(({ spuId }) => spuId) : undefined,
              });
            }}
          >
            {t('aide.仅看已选')}
          </Checkbox>
        )}
      </header>
      <div>
        <Toolbar>
          {list && list.length > 0 ? (
            <Checkbox
              checked={allChecked}
              indeterminate={allIndeterminate}
              onChange={({ target: { checked } }) => {
                onChange(getItemsCheckedNext(value, list, checked));
              }}
            >
              {t('voucher.本页全选')}
            </Checkbox>
          ) : (
            <div />
          )}
          <Input.Group compact>
            <Select
              value={keywordType}
              onChange={setKeywordType}
              options={Object.entries(keywordTypeLabelMap).map(([value, label]) => {
                return {
                  value: parseInt(value, 10),
                  label,
                };
              })}
            />
            <Input
              allowClear
              placeholder={t('trade.请输入')}
              value={keyword}
              onChange={({ target: { value } }) => setKeyword(value)}
              onPressEnter={() => refreshList({ pageNo: 1 })}
              suffix={
                <SearchOutlined
                  style={{ cursor: 'pointer' }}
                  onClick={() => refreshList({ pageNo: 1 })}
                />
              }
              style={{ width: '250px' }}
            />
          </Input.Group>
        </Toolbar>
        <section>
          {(() => {
            if (!list || list.length === 0) {
              if (loading) {
                return <LoadingOutlined />;
              }

              return <Empty />;
            }

            return list.map((item) => {
              const { internalItemId, externalShopId } = item;

              const { [externalShopId]: storeName } = storeNameMap;

              return <Item key={internalItemId} item={item} storeName={storeName} />;
            });
          })()}
        </section>
        {page.total > 0 && (
          <Footer>
            <Pagination
              size="small"
              {...page}
              showSizeChanger
              showTotal={(x) => t('feedback.共x项', { x })}
              onChange={(pageNo, pageSize) => refreshList({ pageNo, pageSize })}
            />
          </Footer>
        )}
      </div>
    </Container>
  );
}

export default ItemList;
